import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FactCheckRounded, InfoOutlined, SearchRounded } from '@mui/icons-material';
import { useTenantStore } from '../../../stores/tenant.store';
import { SectionWorkDetailDialog } from './SectionWorkDetailDialog';
import { SortableCell, useTableSort, useTextFilter } from '../../../components/table/useTableSort';
import {
  programEnrollmentApi,
  type TeachingCoverageRow,
} from '../../../lib/api/program-enrollment.api';
import { formatDate } from '../../../lib/i18n-format';

/**
 * COBERTURA DE REGISTRO — la primera pantalla de la fase de reportes.
 *
 * No mide alumnos: mide si el curso está pasando por la plataforma. Se puso
 * primero porque el dato de producción lo exigió — 36 de 36 alumnos con trabajo
 * "vencido" no es morosidad masiva, es registro incompleto —, y porque de su
 * resultado depende que los informes siguientes se puedan creer.
 *
 * La fila lleva al curso: un informe que obliga a anotar nombres en un papel
 * para ir a buscarlos después se deja de mirar al tercer mes.
 */

/**
 * Verde ≥ 80, ámbar ≥ 50, rojo bajo eso.
 *
 * Dos funciones y no una porque los dos controles no aceptan la misma paleta: el
 * chip tiene `default` para el caso sin dato y la barra no, así que el ternario
 * en el JSX no alcanzaba — el tipo de retorno seguía incluyendo `default`.
 */
function coverageLevel(c: number): 'success' | 'warning' | 'error' {
  if (c >= 0.8) return 'success';
  if (c >= 0.5) return 'warning';
  return 'error';
}
type ChipColor = 'success' | 'warning' | 'error' | 'default' | 'info';

function chipColor(c: number | null): 'success' | 'warning' | 'error' | 'default' {
  return c === null ? 'default' : coverageLevel(c);
}
function barColor(c: number | null): 'success' | 'warning' | 'error' | 'inherit' {
  return c === null ? 'inherit' : coverageLevel(c);
}

export function TeachingCoverageTab({ termId }: { termId: string | null }): JSX.Element {
  const [detailSectionId, setDetailSectionId] = useState<string | null>(null);
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  // El período viene de la página: es el mismo selector que usan Académico,
  // Financiero y Exportar. Tener uno propio acá ponía DOS selectores en pantalla
  // que podían decir cosas distintas.
  const reportQ = useQuery({
    queryKey: ['teaching-coverage', currentTenantId, termId],
    enabled: !!currentTenantId && !!termId,
    queryFn: () =>
      programEnrollmentApi.getTeachingCoverage({ tenantId: currentTenantId!, termId: termId! }),
    staleTime: 60_000,
    // Sin reintentos: el fallo típico acá es que el backend desplegado todavía
    // no tiene el callable, y eso no mejora insistiendo. Cinco POST idénticos
    // en la consola esconden el error real, que es uno solo.
    retry: false,
  });
  const report = reportQ.data;
  // Los borradores no viajan en los totales del informe: se derivan acá porque
  // sólo la cabecera los muestra agregados.
  const totalDraftsEmpty = report?.rows.reduce((sum, r) => sum + r.draftsEmpty, 0) ?? 0;
  const totalDraftsStarted = report?.rows.reduce((sum, r) => sum + r.draftsStarted, 0) ?? 0;

  // Filtro primero, orden después: ordenar lo que no se va a mostrar es trabajo
  // perdido, y el filtro no debe cambiar el criterio de orden elegido.
  const filter = useTextFilter(report?.rows ?? [], (r) => [
    r.courseName,
    r.sectionCode,
    r.facultyName,
  ]);
  const sort = useTableSort(
    filter.rows,
    {
      course: (r) => r.courseName,
      faculty: (r) => r.facultyName,
      students: (r) => r.students,
      due: (r) => r.dueAssignments,
      recorded: (r) => r.recorded,
      ungraded: (r) => r.ungraded,
      coverage: (r) => r.coverage,
      lastActivity: (r) => r.lastActivityAt,
    },
    // SIN ORDEN PROPIO: manda el que trae el informe. El servidor pone
    // primero las secciones SIN TRABAJO CARGADO, que es el hallazgo, y eso es
    // parte del informe y no una preferencia de la tabla. Ordenar acá por
    // cobertura lo descartaba y devolvía el peor caso al final, escondido entre
    // los cursos que van bien. Las columnas siguen siendo ordenables a mano.
    { key: null },
  );

  // EL CHIP DICE EN QUÉ SITUACIÓN ESTÁ, no sólo si hay número. «Sin datos»
  // mostraba igual al curso que cargó su trabajo temprano y al que no cargó
  // nada — el estado deseado y el problema, idénticos en pantalla.
  const stateChip = (row: TeachingCoverageRow): { label: string; color: ChipColor } => {
    if (row.state === 'no_work')
      return { label: t('reports.coverage.state.noWork'), color: 'error' };
    if (row.state === 'nothing_due')
      return { label: t('reports.coverage.state.nothingDue'), color: 'info' };
    if (row.state === 'no_students')
      return { label: t('reports.coverage.state.noStudents'), color: 'default' };
    return { label: `${Math.round((row.coverage ?? 0) * 100)} %`, color: chipColor(row.coverage) };
  };

  return (
    <Stack spacing={3}>
      {/* UNA sola estructura: antes el subtítulo, el selector y el total ocupaban
          tres filas completas y empujaban la tabla —que es lo que se viene a
          ver— fuera de la primera pantalla. La definición de la métrica pasa al
          ícono de ayuda; lo que queda visible es lo que cambia con los datos. */}
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          {report && (
            <>
              {/* LAS DOS CIFRAS PESAN IGUAL, y es el punto del informe: el
                  porcentaje se calcula SOBRE LOS CURSOS QUE CARGARON TRABAJO,
                  así que solo no dice nada de los que no cargaron ninguno. Como
                  chip al lado del número grande se leía como un detalle; son la
                  misma pregunta contestada por dos lados. */}
              <Stack direction="row" spacing={3} alignItems="flex-start">
                <Box>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      lineHeight={1}
                      // El símbolo NO se parte a una segunda línea.
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {report.totals.coverage === null
                        ? t('reports.coverage.noData')
                        : `${Math.round(report.totals.coverage * 100)} %`}
                    </Typography>
                    <Tooltip title={t('reports.coverage.subtitle')}>
                      <InfoOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
                    </Tooltip>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('reports.coverage.totalDetail', {
                      recorded: report.totals.recorded,
                      expected: report.totals.expected,
                    })}
                  </Typography>
                </Box>

                {report.totals.sectionsWithoutWork > 0 && (
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      lineHeight={1}
                      color="error.main"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {t('reports.coverage.withoutWorkCount', {
                        without: report.totals.sectionsWithoutWork,
                        total: report.totals.sectionsWithStudents,
                      })}
                    </Typography>
                    <Typography variant="caption" color="error.main" display="block">
                      {t('reports.coverage.withoutWorkLabel')}
                    </Typography>
                  </Box>
                )}
              </Stack>

              <Box sx={{ flex: 1 }} />

              <TextField
                size="small"
                placeholder={t('reports.coverage.searchPlaceholder')}
                value={filter.query}
                onChange={(e) => filter.setQuery(e.target.value)}
                sx={{ minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {report.totals.ungraded > 0 && (
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    label={t('reports.coverage.ungradedChip', { count: report.totals.ungraded })}
                  />
                )}
                {totalDraftsEmpty > 0 && (
                  <Tooltip title={t('reports.coverage.draftsEmptyHelp')}>
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={t('reports.coverage.draftsEmptyChip', { count: totalDraftsEmpty })}
                    />
                  </Tooltip>
                )}
                {totalDraftsStarted > 0 && (
                  <Tooltip title={t('reports.coverage.draftsStartedHelp')}>
                    <Chip
                      size="small"
                      color="warning"
                      label={t('reports.coverage.draftsStartedChip', { count: totalDraftsStarted })}
                    />
                  </Tooltip>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </Paper>

      {/* La cobertura se mide CONTRA UN PERÍODO: sin uno elegido no hay nada
          que calcular, y quedarse en blanco parecería un error de carga. */}
      {!termId && (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <FactCheckRounded sx={{ fontSize: 60, opacity: 0.2, mb: 2 }} />
          <Typography variant="h6">{t('reports.coverage.pickTerm')}</Typography>
        </Box>
      )}

      {reportQ.isLoading && (
        <Stack alignItems="center" py={6}>
          <CircularProgress />
        </Stack>
      )}

      {reportQ.isError && <Alert severity="error">{t('reports.coverage.loadError')}</Alert>}

      {report && report.rows.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <FactCheckRounded sx={{ fontSize: 60, opacity: 0.2, mb: 2 }} />
          <Typography variant="h6">{t('reports.coverage.emptyTerm')}</Typography>
        </Box>
      )}

      {/* Sin resultados por el FILTRO es distinto de un período sin secciones:
          decirle "no hay secciones" a quien acaba de escribir un nombre lo manda
          a buscar un problema que no existe. */}
      {report && report.rows.length > 0 && sort.rows.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Typography variant="body2">
            {t('reports.coverage.emptyFilter', { query: filter.query })}
          </Typography>
        </Box>
      )}

      {report && sort.rows.length > 0 && (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortableCell columnKey="course" sort={sort}>
                    {t('reports.coverage.colSection')}
                  </SortableCell>
                  <SortableCell columnKey="faculty" sort={sort}>
                    {t('reports.coverage.colFaculty')}
                  </SortableCell>
                  <SortableCell columnKey="students" sort={sort} align="right">
                    {t('reports.coverage.colStudents')}
                  </SortableCell>
                  <SortableCell columnKey="due" sort={sort} align="right">
                    {t('reports.coverage.colDue')}
                  </SortableCell>
                  <SortableCell columnKey="recorded" sort={sort} align="right">
                    {t('reports.coverage.colRecorded')}
                  </SortableCell>
                  <SortableCell columnKey="ungraded" sort={sort} align="right">
                    {t('reports.coverage.colUngraded')}
                  </SortableCell>
                  <SortableCell columnKey="coverage" sort={sort} sx={{ minWidth: 160 }}>
                    {/* La advertencia contra la lectura equivocada vive JUNTO AL
                        NÚMERO, no en el subtítulo: ahí se lee una vez y después
                        se saltea, y acá aparece cuando alguien se pregunta qué
                        significa un 48 %. */}
                    <Tooltip title={t('reports.coverage.coverageHelp')}>
                      <span>{t('reports.coverage.colCoverage')}</span>
                    </Tooltip>
                  </SortableCell>
                  <SortableCell columnKey="lastActivity" sort={sort}>
                    {t('reports.coverage.colLastActivity')}
                  </SortableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sort.rows.map((row: TeachingCoverageRow) => (
                  <TableRow
                    key={row.sectionId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    // DÓNDE LLEVA EL CLIC DEPENDE DE QUÉ HAY QUE HACER. Con
                    // trabajo vencido, lo útil es VER QUÉ FALTA: abre el
                    // detalle. Sin nada cargado no hay detalle que mostrar y lo
                    // que corresponde es ir al curso a cargarlo.
                    onClick={() =>
                      row.state === 'measured'
                        ? setDetailSectionId(row.sectionId)
                        : window.open(`/curso/${row.sectionId}?tab=assignments`, '_self')
                    }
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.courseName}
                      </Typography>
                      {row.sectionCode && (
                        <Typography variant="caption" color="text.secondary">
                          {row.sectionCode}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.facultyName ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{row.students}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('reports.coverage.dueHelp', { total: row.assignments })}>
                        <span>{row.dueAssignments}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {row.state === 'measured' ? `${row.recorded} / ${row.expected}` : '—'}
                      {/* CUÁNTO FALTA, EN PERSONAS Y NO EN PORCENTAJE. «60 %»
                          obliga a calcular de cabeza qué significa, y el
                          director lo leyó como un reproche a su corrección
                          cuando hablaba de entregas que nunca llegaron. */}
                      {row.state === 'measured' && row.expected > row.recorded && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('reports.coverage.notRecorded', {
                            count: row.expected - row.recorded,
                          })}
                        </Typography>
                      )}
                      {row.draftsStarted > 0 && (
                        <Typography variant="caption" color="warning.main" display="block">
                          {t('reports.coverage.draftsStarted', { count: row.draftsStarted })}
                        </Typography>
                      )}
                      {row.draftsEmpty > 0 && (
                        <Typography variant="caption" color="text.disabled" display="block">
                          {t('reports.coverage.draftsEmpty', { count: row.draftsEmpty })}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {/* UN GUION NO DICE «ESTÁS AL DÍA»: dice «no hay dato», y
                          son cosas distintas. Que el docente corrigió todo lo
                          que le entregaron es una BUENA noticia y el informe se
                          la callaba, dejando que el porcentaje de cobertura
                          —que mide otra cosa— pareciera su culpa. */}
                      {row.state !== 'measured' ? (
                        '—'
                      ) : row.ungraded > 0 ? (
                        row.ungraded
                      ) : row.recorded > 0 ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="success"
                          label={t('reports.coverage.upToDate')}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {/* BARRA SÓLO CUANDO HAY ALGO QUE MEDIR. Una barra
                            vacía al lado de «Sin trabajo cargado» dibujaba un
                            cero que nadie midió, y ocupaba el ancho que el
                            texto del estado necesita para no cortarse. */}
                        {row.state === 'measured' && (
                          <LinearProgress
                            variant="determinate"
                            value={Math.round((row.coverage ?? 0) * 100)}
                            color={barColor(row.coverage)}
                            sx={{ flex: 1, height: 6, borderRadius: 3 }}
                          />
                        )}
                        <Chip
                          size="small"
                          label={stateChip(row).label}
                          color={stateChip(row).color}
                          variant={row.state === 'measured' ? 'filled' : 'outlined'}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {row.lastActivityAt ? formatDate(row.lastActivityAt) : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {termId && (
        <SectionWorkDetailDialog
          sectionId={detailSectionId}
          termId={termId}
          onClose={() => setDetailSectionId(null)}
        />
      )}
    </Stack>
  );
}
